import { Component, Input, ContentChild, TemplateRef, HostListener, ElementRef, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../../services/layout.service';

@Component({
  selector: 'app-page-title',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="page-title-container"
         [class.sticky-header]="isSticky && sticky"
         [style.right.px]="isSticky && sticky && isSidePaneVisible ? 320 : null">
      <div class="title-section">
        <ng-container *ngIf="titleTemplate; else defaultTitle">
          <ng-container [ngTemplateOutlet]="titleTemplate"></ng-container>
        </ng-container>
        <ng-template #defaultTitle>
          <h1>{{ title }}</h1>
        </ng-template>
      </div>
      <div class="actions-section" *ngIf="actionTemplate">
        <ng-container [ngTemplateOutlet]="actionTemplate"></ng-container>
      </div>
    </div>
    <!-- Placeholder to maintain layout when header becomes fixed -->
    <div class="sticky-placeholder" *ngIf="isSticky && sticky" [style.height.px]="headerHeight"></div>
  `,
  styles: [
    `
      .page-title-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1rem;
        height: 56px;
        background-color: white;
        border-radius: 7px;
        border: 1px solid #e2e8f0;
        padding: 0 14px;
        transition: all 0.3s ease;

        .title-section {
          display: flex;
          align-items: center;
          gap: .75rem;
          flex: 1;
          min-width: 0;

          ::ng-deep & h1 {
            margin: 0;
            font-size: 20px;
            font-weight: 700;
            color: #334155;
          }
        }

        .actions-section {
          display: flex;
          gap: 0.5rem;
          align-items: center;
          flex-shrink: 0;
        }

        &.sticky-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          margin-bottom: 0;
          border-radius: 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
      }

      .sticky-placeholder {
        margin-bottom: 1rem;
      }
    `,
  ],
})
export class PageTitleComponent implements OnInit, OnDestroy {
  @Input() title?: string;
  @Input() sticky: boolean = false;
  @ContentChild('title') titleTemplate?: TemplateRef<any>;
  @ContentChild('actions') actionTemplate?: TemplateRef<any>;

  isSticky = false;
  headerHeight = 56;
  isSidePaneVisible = false;
  private scrollThreshold = 100;
  private subscription?: Subscription;

  constructor(
    private elementRef: ElementRef,
    private layoutService: LayoutService
  ) {}

  ngOnInit() {
    if (this.sticky) {
      // Get the actual height of the header element
      setTimeout(() => {
        const headerElement = this.elementRef.nativeElement.querySelector('.page-title-container');
        if (headerElement) {
          this.headerHeight = headerElement.offsetHeight;
        }
      });
    }

    // Subscribe to right panel visibility changes
    this.subscription = this.layoutService.isVisible$.subscribe(
      isVisible => {
        this.isSidePaneVisible = isVisible;
      }
    );
  }

  ngOnDestroy() {
    // Unsubscribe to prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (!this.sticky) return;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // Only make sticky if scrolled past the threshold
    this.isSticky = scrollTop > this.scrollThreshold;
  }
}
